/**
 * Road Constants
 * Centralized constants for Polish road system classification and validation
 */

// Road Number Ranges
export const ROAD_NUMBER_RANGES = {
  NATIONAL: {
    MIN: 1,
    MAX: 94,
  },
  VOIVODESHIP: {
    MIN: 100,
    MAX: 999,
  },
  MOTORWAY: ['1', '2', '4'], // A1, A2, A4
  EXPRESSWAY: {
    MIN: 1,
    MAX: 99,
  },
} as const;

// Road Type Enums
export enum RoadType {
  MOTORWAY = 'motorway',
  EXPRESSWAY = 'expressway',
  NATIONAL_ROAD = 'nationalRoad',
  VOIVODESHIP_ROAD = 'voivodeshipRoad',
}

export enum RoadClassification {
  NATIONAL = 'national',
  VOIVODESHIP = 'voivodeship',
}

// Road Prefixes
export const ROAD_PREFIXES = {
  MOTORWAY: 'A',
  EXPRESSWAY: 'S',
  NATIONAL: 'DK',
  VOIVODESHIP: 'DW',
} as const;

// Road Pattern Definitions
export const ROAD_PATTERNS = {
  // Complete road references (with prefixes)
  MOTORWAY: /^(A[1-4])\b/i,
  EXPRESSWAY: /^(S\d{1,2})\b/i,
  NATIONAL_PREFIXED: /^(DK\d{1,2})\b/i,
  VOIVODESHIP_PREFIXED: /^(DW\d{3})\b/i,

  // Plain numbers
  THREE_DIGIT_NUMBER: /^(\d{3})\b/,
  ONE_TWO_DIGIT_NUMBER: /^(\d{1,2})\b/,

  // Validation patterns
  COMPLETE_ROAD_REFERENCE: /^(A\d+|S\d+|DK\d+|DW\d+)$/i,
} as const;

// Translation Patterns for Old Format Recognition
export const TRANSLATION_PATTERNS = [
  /Autostrada\s+([A]\d+)/i,       // "Autostrada A1"
  /Droga\s+ekspresowa\s+([S]\d+)/i, // "Droga ekspresowa S1"
  /Droga\s+krajowa\s+nr\s+(\d+)/i,  // "Droga krajowa nr 1"
  /Droga\s+wojewódzka\s+nr\s+(\d+)/i, // "Droga wojewódzka nr 100"
  /Droga\s+krajowa\s+([A]\d+)/i,    // "Droga krajowa A1"
  /Droga\s+krajowa\s+([S]\d+)/i,    // "Droga krajowa S1"
  /Droga\s+krajowa\s+(DK\d+)/i,     // "Droga krajowa DK1"
  /Droga\s+wojewódzka\s+(DW\d+)/i,  // "Droga wojewódzka DW305"
] as const;

// Road Type Colors for UI
export const ROAD_TYPE_COLORS = {
  [RoadType.MOTORWAY]: 'blue',
  [RoadType.EXPRESSWAY]: 'green',
  [RoadType.NATIONAL_ROAD]: 'red',
  [RoadType.VOIVODESHIP_ROAD]: 'orange',
} as const;

// Translation Keys
export const ROAD_TRANSLATION_KEYS = {
  [RoadType.MOTORWAY]: 'roads.motorway',
  [RoadType.EXPRESSWAY]: 'roads.expressway',
  [RoadType.NATIONAL_ROAD]: 'map.fallbacks.nationalRoad',
  [RoadType.VOIVODESHIP_ROAD]: 'map.fallbacks.voivodeshipRoad',
} as const;

// Type Guards
export const isMotorwayNumber = (num: string): boolean =>
  (ROAD_NUMBER_RANGES.MOTORWAY as readonly string[]).includes(num);

export const isExpresswayNumber = (num: number): boolean =>
  num >= ROAD_NUMBER_RANGES.EXPRESSWAY.MIN && num <= ROAD_NUMBER_RANGES.EXPRESSWAY.MAX;

export const isNationalRoadNumber = (num: number): boolean =>
  num >= ROAD_NUMBER_RANGES.NATIONAL.MIN && num <= ROAD_NUMBER_RANGES.NATIONAL.MAX;

export const isVoivodeshipRoadNumber = (num: number): boolean =>
  num >= ROAD_NUMBER_RANGES.VOIVODESHIP.MIN && num <= ROAD_NUMBER_RANGES.VOIVODESHIP.MAX;